import { useIntl } from 'react-intl';

import TrashIcon from '@/assets/icons/trash.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { ZIPRECRUITER } from '@/types/channels';

import { getChannelDefaults } from '../channel-defaults';
import { ZIPRECRUITER_BUDGET_PRESETS } from '../validation/constants';
import { useChannelFormContext } from './channel-form-context';

interface OpeningBudgetProps {
  channelSlug: string;
  openingId: string;
  showDeleteButton: boolean;
}

export function OpeningBudget({
  channelSlug,
  openingId,
  showDeleteButton,
}: OpeningBudgetProps) {
  const { recalculateChannelBudget, removeOpeningFromChannel } =
    useChannelFormContext();
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const {
    control,
    register,
    getValues,
    formState: { errors },
  } = hookForm;

  const channelDefaults = getChannelDefaults(channelSlug);
  const isBudgetReadOnly = !!channelDefaults.budget;
  const errorMessage =
    errors?.channels?.[channelSlug]?.openingBudgets?.[openingId]?.message;

  return (
    <div key={openingId} className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {showDeleteButton && (
          <Button
            type="button"
            variant="icon"
            size="icon"
            onClick={() => removeOpeningFromChannel(openingId)}
          >
            <img src={TrashIcon} alt="Delete" />
          </Button>
        )}

        <Typography variant="body1" className="min-w-0 flex-1 truncate">
          {getValues(`jobDetails.${openingId}.title`)}
        </Typography>

        {channelSlug === ZIPRECRUITER ? (
          <FormSelect
            control={control}
            options={ZIPRECRUITER_BUDGET_PRESETS}
            isMultiSelect={false}
            className="rounded-sm"
            placeholder={intl.formatMessage({
              defaultMessage: 'Select Spend',
              description:
                'ZipRecruiter multi-opening flow: pick a spend tier for this job',
            })}
            {...register(
              `channels.${channelSlug}.openingBudgets.${openingId}`,
              {
                onChange: () => recalculateChannelBudget(),
              },
            )}
          />
        ) : (
          <Input
            isNumber
            {...register(
              `channels.${channelSlug}.openingBudgets.${openingId}`,
              {
                setValueAs: value =>
                  value === '' || value === null ? null : Number(value),
                onChange: () => recalculateChannelBudget(),
              },
            )}
            placeholderLabel="$"
            placeholder={intl.formatMessage({
              defaultMessage: 'Budget',
              description:
                'Per-opening dollar cap when posting a job to this channel',
            })}
            className="ml-auto !w-25 shrink-0"
            error={errorMessage}
            showError={false}
            readOnly={isBudgetReadOnly}
          />
        )}
      </div>
      {errorMessage && (
        <Typography variant="small1" className="!text-fx-rose text-right">
          {errorMessage}
        </Typography>
      )}
    </div>
  );
}
